<template>
  <section id="calculator" class="research-calculator research-section" aria-labelledby="calculator-heading">
    <h2 id="calculator-heading">{{ type === 'software' ? 'What would this software stack cost for your team?' : 'What hourly rate covers your assumptions?' }}</h2>
    <p class="research-note">Workspace369 calculation · not observed data</p>
    <p>Change the assumptions to reproduce the model. Your inputs stay in this browser; they are not submitted.</p>
    <div class="research-calculator-fields" v-if="type === 'software'">
      <div><label for="research-stack">Software bundle</label>
        <select id="research-stack" v-model="stack"><option v-for="item in softwareStacks" :key="item.id" :value="item.id">{{ item.name }}</option></select>
      </div>
      <div><label for="research-team">Team size</label>
        <select id="research-team" v-model.number="team"><option v-for="count in [1, 5, 10, 25]" :key="count" :value="count">{{ count }} {{ count === 1 ? 'person' : 'people' }}</option></select>
      </div>
      <div><label for="research-billing">Billing terms</label>
        <select id="research-billing" v-model="billing"><option value="annual">Annual commitment</option><option value="monthly">Monthly billing</option></select>
      </div>
    </div>
    <div class="research-calculator-fields" v-else>
      <div><label for="research-size">BLS establishment-size benchmark</label>
        <select id="research-size" v-model.number="size"><option v-for="(item, i) in laborGroups" :key="item.size" :value="i">{{ item.size }}</option></select>
      </div>
      <label>Billable utilization (%)<input v-model.number="utilization" type="number" min="1" max="100" inputmode="decimal" /></label>
      <label>Nonlabor overhead (% of labor cost)<input v-model.number="overhead" type="number" min="0" max="200" inputmode="decimal" /></label>
      <label>Target margin (% of revenue)<input v-model.number="margin" type="number" min="0" max="99" inputmode="decimal" /></label>
    </div>
    <div class="research-calculator-result" aria-live="polite" aria-atomic="true">
      <div v-show="result !== null">
        <span>{{ type === 'software' ? 'Modeled base subscription' : 'Modeled billable-rate floor' }}</span>
        <strong>{{ usd(result) }}<small>{{ type === 'software' ? ' / month' : ' / billable hour' }}</small></strong>
        <p v-if="type === 'software'"><span>12-month base total:</span> <span>{{ usd(result * 12) }}</span><br /><span v-show="billing === 'annual'">Monthly equivalent of annual commitments, not necessarily monthly billing.</span><span v-show="billing === 'monthly'">Assumes monthly prices remain unchanged for 12 months.</span></p>
        <p v-else><span>Benchmark compensation per hour worked:</span> <span>{{ usd(laborGroups[size].values[0]) }}</span><br /><span>This is a cost model, not a market-rate recommendation.</span></p>
      </div>
      <p v-show="result === null" role="alert">Enter utilization from 1–100%, overhead from 0–200%, and margin from 0–99%.</p>
    </div>
    <p class="research-note">{{ type === 'software' ? 'Formula: sum of plan price × billable seats. Minimum seats apply. Base prices only; taxes, usage, payment processing and add-ons are excluded.' : 'Formula: compensation × (1 + overhead) ÷ utilization ÷ (1 − margin), with percentages expressed as fractions. Paid leave is already included in compensation.' }}</p>
    <a href="#methodology">Read the model assumptions and limitations →</a>
  </section>
</template>

<script setup>
import { laborFloor, laborGroups, softwareStacks, stackCost } from '~/data/research/reports.mjs';
const props = defineProps({ type: { type: String, required: true } });
const stack = ref('essentials');
const team = ref(5);
const billing = ref('annual');
const size = ref(0);
const utilization = ref(70);
const overhead = ref(20);
const margin = ref(20);
const result = computed(() => props.type === 'software' ? stackCost(stack.value, team.value, billing.value) : laborFloor(laborGroups[size.value].values[0], utilization.value, overhead.value, margin.value));
const usd = (value) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(value);
</script>
